import React from 'react';
import { motion } from 'framer-motion';
import { DEPOSIT_POLICY } from '../data/pricing';

const Pricing = () => {
  const quickWashPackage = {
    title: 'QUICK HAND WASH',
    subtitle: '1-STEP EXTERIOR WASH + QUICK VACUUM',
    features: [
      { text: '1-STEP EXTERIOR HAND WASH', included: true },
      { text: 'WHEEL & TIRE RINSE', included: true },
      { text: 'WINDOW WIPE-DOWN', included: true },
      { text: 'QUICK INTERIOR VACUUM', included: true },
    ],
    popular: false,
  };

  const monthlyWashPackage = {
    title: 'MAINTENANCE WASH',
    subtitle: '4X / MONTH — FULL INTERIOR & EXTERIOR REFRESH',
    features: [
      { text: '4 WASHES PER MONTH', included: true },
      { text: 'FULL EXTERIOR HAND WASH', included: true },
      { text: 'FULL INTERIOR REFRESH', included: true },
      { text: 'WHEEL & TIRE CLEANING', included: true },
      { text: 'WINDOW CLEANING', included: true },
      { text: 'VACUUM & WIPE-DOWN', included: true },
    ],
    popular: false,
  };

  const mainPackages = [
    {
      title: 'STAGE 1',
      subtitle: 'Full interior + exterior detail — perfect base reset for most vehicles.',
      features: [
        { text: 'HAND WASH (2 STAGE)', included: true },
        { text: 'WHEEL AND TIRE CLEANING', included: true },
        { text: 'WINDOW CLEANING', included: true },
        { text: 'DOOR JAMS', included: true },
        { text: 'INTERIOR STEAM CLEANING', included: true },
        { text: 'SHAMPOO EXTRACTION', included: true },
        { text: 'SEAT CLEANING', included: true },
        { text: 'DASH & UPHOLSTERY WIPE-DOWN', included: true },
        { text: '', included: false },
      ],
      popular: true,
    },
    {
      title: 'STAGE 2',
      subtitle: 'Everything in Stage 1, plus clay bar and a 1-step polish to remove light swirls and boost gloss.',
      features: [
        { text: 'HAND WASH (2 STAGE)', included: true },
        { text: 'WHEEL AND TIRE CLEANING', included: true },
        { text: 'WINDOW CLEANING', included: true },
        { text: 'DOOR JAMS', included: true },
        { text: 'INTERIOR STEAM CLEANING', included: true },
        { text: 'SHAMPOO EXTRACTION', included: true },
        { text: 'SEAT CLEANING', included: true },
        { text: 'DASH & UPHOLSTERY WIPE-DOWN', included: true },
        { text: '1 STEP PAINT CORRECTION', included: true },
        { text: 'CLAY BAR', included: true },
      ],
      popular: true,
    },
    {
      title: 'STAGE 3',
      subtitle: 'Full detail, 1-step correction, and 6 year ceramic coating protection for long-term shine.',
      features: [
        { text: 'HAND WASH (2 STAGE)', included: true },
        { text: 'WHEEL AND TIRE CLEANING', included: true },
        { text: 'WINDOW CLEANING', included: true },
        { text: 'DOOR JAMS', included: true },
        { text: 'INTERIOR STEAM CLEANING', included: true },
        { text: 'SHAMPOO EXTRACTION', included: true },
        { text: 'SEAT CLEANING', included: true },
        { text: 'DASH & UPHOLSTERY WIPE-DOWN', included: true },
        { text: '1 STEP PAINT CORRECTION', included: true },
        { text: 'CLAY BAR', included: true },
        { text: '6-YEAR CERAMIC COATING', included: true },
      ],
      popular: true,
    },
  ];

  const enhancementsPackage = {
    title: 'ENHANCEMENTS',
    subtitle: 'ADDITIONAL SERVICES',
    features: [
      { text: 'PAINT CORRECTION', included: true },
      { text: '6-YEAR CERAMIC COATING', included: true },
      { text: 'EXTERIOR CERAMIC SEALER', included: true },
      { text: 'ENGINE BAY CLEANING', included: true },
    ],
    popular: false,
  };

  const headlightRestorationPackage = {
    title: 'HEADLIGHT RESTORATION',
    subtitle: 'RESTORE CLARITY & NIGHT VISIBILITY',
    features: [
      { text: 'SAND & POLISH LENSES', included: true },
      { text: 'REMOVE YELLOWING & OXIDATION', included: true },
      { text: 'UV SEALANT PROTECTION', included: true },
    ],
    popular: false,
  };

  const ceramicCoatingWheelsPackage = {
    title: 'CERAMIC COATING — WHEELS',
    subtitle: 'LONG-LASTING WHEEL PROTECTION',
    features: [
      { text: 'DEEP CLEAN & DECONTAMINATION', included: true },
      { text: 'CERAMIC COATING ON WHEELS', included: true },
      { text: 'HYDROPHOBIC, EASY-TO-CLEAN FINISH', included: true },
    ],
    popular: false,
  };

  const scrollToBookingForm = (key) => {
    if (typeof window === 'undefined') return;

    window.dispatchEvent(
      new CustomEvent('nasPackageSelect', { detail: key })
    );

    const isDesktop = window.innerWidth >= 1024;
    const targetId = isDesktop ? 'hero-quote' : 'contact';
    const target = document.getElementById(targetId);

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.location.href = isDesktop ? '/#hero-quote' : '/#contact';
  };

  const renderPackageCard = (pkg) => (
    <div
      className={`relative ${
        pkg.popular
          ? 'bg-gradient-to-br from-zinc-950/90 to-black/95 border-2 border-yellow-400/60'
          : 'bg-gradient-to-br from-black/80 via-zinc-950/80 to-yellow-900/10 border border-yellow-400/20'
      } backdrop-blur-sm p-4 md:p-6 rounded-lg transform hover:scale-[1.02] hover:border-yellow-400/80 transition-all duration-300 flex flex-col h-full w-full`}
    >
      <div className="relative mb-4 md:mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
          {pkg.title}
        </h3>
        <p className="text-yellow-400 text-xs md:text-sm font-semibold mb-3 md:mb-4">
          {pkg.subtitle}
        </p>
      </div>
      <ul className="space-y-2 md:space-y-2.5 mb-4 md:mb-6 flex-grow">
        {pkg.features.map((feature, idx) =>
          feature.text ? (
            <li key={idx} className="flex items-start text-white/90 text-xs">
              <svg
                className="w-4 h-4 text-yellow-400 mr-2 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="break-words">{feature.text}</span>
            </li>
          ) : (
            <li key={idx} className="h-4 md:h-5"></li>
          )
        )}
      </ul>

      <button
        type="button"
        onClick={() => scrollToBookingForm(pkg.title)}
        className={`block w-full py-2 md:py-3 text-center mt-auto ${
          pkg.popular
            ? 'bg-yellow-400 text-black hover:bg-yellow-300'
            : 'bg-black/50 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-400/10'
        } font-semibold rounded-lg transition-all duration-300 text-sm md:text-base`}
      >
        Book Now
      </button>
    </div>
  );

  const renderHorizontalServiceCard = (pkg) => (
    <div className="relative bg-gradient-to-br from-black/80 via-zinc-950/80 to-yellow-900/10 border border-yellow-400/20 backdrop-blur-sm p-4 md:p-6 rounded-lg hover:border-yellow-400/80 transition-all duration-300">
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,240px)_1fr_auto] lg:items-center lg:gap-6">
        <div className="min-w-0">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
            {pkg.title}
          </h3>
          <p className="text-yellow-400 text-xs md:text-sm font-semibold">
            {pkg.subtitle}
          </p>
        </div>

        <ul className="flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-2 min-w-0">
          {pkg.features
            .filter((f) => f.text)
            .map((feature, idx) => (
              <li
                key={idx}
                className="flex items-center text-white/90 text-xs"
              >
                <svg
                  className="w-4 h-4 text-yellow-400 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>{feature.text}</span>
              </li>
            ))}
        </ul>

        <button
          type="button"
          onClick={() => scrollToBookingForm(pkg.title)}
          className="flex-shrink-0 w-full lg:w-auto px-6 py-2.5 md:py-3 text-center bg-black/50 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-400/10 font-semibold rounded-lg transition-all duration-300 text-sm md:text-base whitespace-nowrap"
        >
          Book Now
        </button>
      </div>
    </div>
  );

  return (
    <section
      id="pricing"
      className="relative py-12 md:py-24 bg-black overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          animate={{ y: [0, -18, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-0 right-0 w-64 h-64 bg-[#e1b11b]/10 rounded-full blur-3xl opacity-60"
        />
        <motion.div
          animate={{ y: [0, 18, 0] }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          className="absolute bottom-0 left-0 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl opacity-60"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-left mb-8 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold italic mb-4 tracking-tight">
            <span className="text-white">OUR</span>
            <span className="text-yellow-400"> DETAIL PACKAGES</span>
          </h2>
          <p className="text-sm md:text-base text-zinc-400 max-w-3xl leading-relaxed">
            {DEPOSIT_POLICY}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
          {[quickWashPackage, monthlyWashPackage].map((pkg, i) => (
            <div
              key={i}
              className="relative bg-gradient-to-br from-black/80 via-zinc-950/80 to-yellow-900/10 border border-yellow-400/20 backdrop-blur-sm p-5 md:p-6 rounded-lg hover:scale-[1.01] hover:border-yellow-400/80 transition-all duration-300 flex flex-col h-full"
            >
              <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                {pkg.title}
              </h3>
              <p className="text-yellow-400 text-xs md:text-sm font-semibold mb-3">
                {pkg.subtitle}
              </p>
              <ul className="space-y-2 mb-5 flex-grow">
                {pkg.features
                  .filter((f) => f.text)
                  .map((feature, idx) => (
                    <li key={idx} className="flex items-start text-white/90 text-xs">
                      <svg className="w-4 h-4 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature.text}</span>
                    </li>
                  ))}
              </ul>
              <button
                type="button"
                onClick={() => scrollToBookingForm(pkg.title)}
                className="block w-full py-2 md:py-3 text-center bg-black/50 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-400/10 font-semibold rounded-lg transition-all duration-300 text-sm md:text-base mt-auto"
              >
                Book Now
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
          {mainPackages.map((pkg, i) => (
            <div key={i} className="w-full">
              {renderPackageCard(pkg)}
            </div>
          ))}
        </div>

        <div className="w-full space-y-4 md:space-y-6">
          {renderHorizontalServiceCard(enhancementsPackage)}
          {renderHorizontalServiceCard(headlightRestorationPackage)}
          {renderHorizontalServiceCard(ceramicCoatingWheelsPackage)}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
